import { Button, Classes, Dialog, Intent, Spinner, Tabs, Tab } from '@blueprintjs/core'
import { lang, toast } from 'botpress/shared'
import React, { FC, useState, useEffect } from 'react'
import packageJson from '../../../../package.json'
import { encryptPayload } from '../../aes-encryption'
import { jwtKeyStore, formDataStore } from '../../token-store'
import './modern-settings.css'

const CURRENT_VERSION = packageJson.version
const API_URL = process.env.REACT_APP_API_URL || 'https://www.app.xmati.ai/apis'

interface Props {
  isOpen: boolean
  toggle: () => void
}

interface UserEnquiry {
  id: string
  email: string
  enquiry: string
  submittedAt: string
}

const Enquiry: FC<Props> = ({ isOpen, toggle }) => {
  const [enquiryText, setEnquiryText] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [userEnquiries, setUserEnquiries] = useState<UserEnquiry[]>([])
  const [isLoadingEnquiries, setIsLoadingEnquiries] = useState(false)
  const [selectedTab, setSelectedTab] = useState('submit')
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc') // newest first by default
  const token = jwtKeyStore.get() || ''

  const fetchUserEnquiries = async () => {
    setIsLoadingEnquiries(true)
    try {
      // const formData = formDataStore.get() || {}
      // const userEmail = formData.email || ''

      const response = await fetch(`${API_URL}/get-user-enquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-App-Version': CURRENT_VERSION
        }
      })

      const result = await response.json()

      if (result.success) {
        setUserEnquiries(result.data || [])
      } else {
        console.error('Failed to fetch user enquiries:', result.msg)
      }
    } catch (error) {
      console.error('Error fetching user enquiries:', error)
    } finally {
      setIsLoadingEnquiries(false)
    }
  }

  const handleSubmit = async () => {
    if (!enquiryText.trim()) {
      return
    }

    setIsLoading(true)

    try {
      const response = await fetch(`${API_URL}/submit-enquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-App-Version': CURRENT_VERSION
        },
        body: JSON.stringify({
          payload: encryptPayload({
            enquiry: enquiryText,
          })
        })
      })

      const result = await response.json()

      if (result.success) {
        setEnquiryText('')
        toast.success('Enquiry submitted successfully! Our support team will get back to you soon.')
        // Refresh the enquiries list
        await fetchUserEnquiries()
        setSelectedTab('history')
      } else {
        toast.failure(result.msg || 'Failed to submit enquiry. Please try again.')
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error)
      toast.failure('Error submitting enquiry: Please try again later.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading && !isLoadingEnquiries) {
      setEnquiryText('')
      setSelectedTab('submit')
      toggle()
    }
  }

  useEffect(() => {
    if (isOpen) {
      void fetchUserEnquiries()
    }
  }, [isOpen])



  return (
    <>
      <Dialog
        title="Support & Enquiries"
        icon="help"
        isOpen={isOpen}
        onClose={handleClose}
        transitionDuration={0}
        canOutsideClickClose={!isLoading && !isLoadingEnquiries}
        canEscapeKeyClose={!isLoading && !isLoadingEnquiries}
        className="modern-dialog"
        style={{ width: 700, height: '600px' }}
      >
        <div className={Classes.DIALOG_BODY} style={{ padding: '0', height: 'calc(100% - 120px)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Tabs
              id="enquiry-tabs"
              className="modern-tabs"
              selectedTabId={selectedTab}
              onChange={(tabId) => setSelectedTab(tabId as string)}
            >
              <Tab
                id="submit"
                title="Submit New Enquiry"
                panel={
                  <div style={{ padding: '24px 0', height: '390px', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ marginBottom: '15px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: 600,
                        fontSize: '13px',
                        color: '#334155'
                      }}>
                        How can we help you?
                      </label>
                      <textarea
                        value={enquiryText}
                        onChange={(e) => setEnquiryText(e.target.value)}
                        placeholder="Please describe your enquiry, feedback, or any questions you may have..."
                        className="modern-input modern-textarea"
                        style={{ flex: 1 }}
                        disabled={isLoading}
                      />
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#64748b',
                      fontStyle: 'italic',
                      marginTop: '8px'
                    }}>
                      Our support team will review your enquiry and get back to you as soon as possible.
                    </div>
                  </div>
                }
              />
              <Tab
                id="history"
                title={`My Enquiries (${userEnquiries.length})`}
                panel={
                  <div style={{ padding: '20px 0', height: '410px', display: 'flex', flexDirection: 'column' }}>
                    {/* Sort and Refresh buttons at the top */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginBottom: '15px' }}>
                      <Button
                        className="modern-btn-secondary modern-btn-icon"
                        icon={sortOrder === 'desc' ? 'sort-desc' : 'sort-asc'}
                        onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                        disabled={isLoadingEnquiries}
                        title={`Sort ${sortOrder === 'desc' ? 'Oldest First' : 'Newest First'}`}
                      />
                      <Button
                        className="modern-btn-secondary modern-btn-icon"
                        icon="refresh"
                        onClick={() => void fetchUserEnquiries()}
                        disabled={isLoadingEnquiries}
                        loading={isLoadingEnquiries}
                        title="Refresh Enquiries"
                      />
                    </div>

                    <div className="modern-scrollbar" style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
                      {isLoadingEnquiries ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                          <Spinner size={40} />
                        </div>
                      ) : userEnquiries.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                          <p style={{ fontWeight: 500, margin: '0 0 4px 0' }}>No enquiries submitted yet.</p>
                          <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Click on "Submit New Enquiry" tab to submit your first enquiry.</p>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {userEnquiries
                            .sort((a, b) => {
                              const dateA = new Date(a.submittedAt).getTime()
                              const dateB = new Date(b.submittedAt).getTime()
                              return sortOrder === 'desc' ? dateB - dateA : dateA - dateB
                            })
                            .map((enquiry, index) => (
                              <div
                                key={enquiry.id}
                                className="modern-card"
                              >
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: '12px'
                                }}>
                                  <span style={{
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    color: '#0369a1',
                                    backgroundColor: '#e0f2fe',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                  }}>
                                    Enquiry #{sortOrder === 'desc' ? userEnquiries.length - index : index + 1}
                                  </span>
                                  <small style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 500 }}>
                                    {new Date(enquiry.submittedAt).toLocaleDateString()} at{' '}
                                    {new Date(enquiry.submittedAt).toLocaleTimeString()}
                                  </small>
                                </div>
                                <div
                                  style={{
                                    backgroundColor: '#f8fafc',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    borderLeft: '4px solid #04A9E1',
                                    borderTop: '1px solid #e2e8f0',
                                    borderRight: '1px solid #e2e8f0',
                                    borderBottom: '1px solid #e2e8f0',
                                    fontSize: '14px',
                                    lineHeight: '1.5',
                                    color: '#334155'
                                  }}
                                >
                                  {enquiry.enquiry}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                }
              />
            </Tabs>
          </div>
        </div>

        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            {selectedTab === 'submit' && (
              <Button
                onClick={handleSubmit}
                className="modern-btn-primary"
                disabled={!enquiryText.trim() || isLoading}
                loading={isLoading}
              >
                Submit Enquiry
              </Button>
            )}
          </div>
        </div>

        {/* Full screen loader overlay */}
        {isLoading && (
          <div className="modern-loader">
            <Spinner size={50} />
            <div className="modern-loader-text">Submitting your enquiry...</div>
          </div>
        )}
      </Dialog>
    </>
  )
}

export default Enquiry
